import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { AdAgenciesBoard } from '@api/BoardSDK';
import { executeIntegration } from '@api/integration-block.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
const COLS = ['contactName', 'email', 'phone', 'website', 'address', 'borougharea', 'focus', 'outreachStatus', 'lastContacted', 'notes'] as const;
const STATUSES = ['Not Contacted', 'Media Kit Sent', 'Replied', 'Booked Ad', 'Not Interested', 'Bad Email'] as const;

export const getAgencies = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), q: z.string().optional(), status: z.enum(STATUSES).optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new AdAgenciesBoard();
      if (data.cursor) return b.items().withColumns([...COLS]).withPagination({ limit: 25, cursor: data.cursor }).execute();
      const where: Record<string, unknown> = {};
      if (data.q?.trim()) where.name = { contains: data.q.trim() };
      if (data.status) where.outreachStatus = data.status;
      return b.items().withColumns([...COLS]).where(where as never).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 25 }).execute();
    });
  });

export const getAgencyCounts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(() => new AdAgenciesBoard().aggregate().groupBy('outreachStatus').countItems('n').execute());
  });

export const saveAgency = createServerFn({ method: 'POST' })
  .validator(z.object({
    pass: z.string(), id: z.string().optional(), name: z.string().min(1).max(200),
    contactName: z.string().max(200), email: z.string().max(200), phone: z.string().max(40), website: z.string().max(300), notes: z.string().max(4000), address: z.string().max(1000).optional(),
    status: z.enum(STATUSES),
  }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const vals = {
      name: data.name.trim(), contactName: data.contactName || null,
      email: data.email ? { email: data.email.trim(), label: data.email.trim() } : null,
      phone: data.phone ? { phone: data.phone.trim(), country: 'US' } : null,
      website: data.website ? { url: /^https?:/.test(data.website) ? data.website : `https://${data.website}`, label: data.website } : null,
      notes: data.notes || null, address: data.address || null, outreachStatus: data.status,
    };
    return withBoardAuth(async () => {
      const b = new AdAgenciesBoard();
      if (data.id) await b.item(data.id).update(vals).execute();
      else await b.item().create(vals).execute();
      return { ok: true };
    });
  });

/** Sends the pitch to George only, as a test. Does not change the agency's status. */
export const sendSamplePitch = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    await withBoardAuth(() => executeIntegration(GMAIL, { to: ['gbrook8344@gmail.com'], subject: `[SAMPLE] ${data.subject}`, body: data.body }));
    return { ok: true };
  });

export const bulkAddAgencies = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), rows: z.array(z.object({ name: z.string().min(1).max(200), contactName: z.string().max(200), email: z.string().max(200), address: z.string().max(1000) })).min(1).max(200) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new AdAgenciesBoard();
      let added = 0, skipped = 0;
      for (const r of data.rows) {
        const dupe = await b.items().where({ name: { contains: r.name } } as never).withPagination({ limit: 1 }).execute();
        if (dupe.items.length) { skipped++; continue; }
        await b.item().create({ name: r.name, contactName: r.contactName || null, email: r.email.includes('@') ? { email: r.email, label: r.email } : null, address: r.address || null, outreachStatus: 'Not Contacted' }).execute();
        added++;
      }
      return { added, skipped };
    });
  });

function nyDate() { return new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + 'T12:00:00Z'); }

export const pitchAgency = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/), to: z.string().min(3).max(200), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    await withBoardAuth(() => executeIntegration(GMAIL, { to: [data.to], subject: data.subject, body: data.body }));
    await withBoardAuth(() => new AdAgenciesBoard().item(data.id).update({ outreachStatus: 'Media Kit Sent', lastContacted: nyDate() }).execute());
    return { ok: true };
  });
